import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  People as PeopleIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
  HourglassEmpty as HourglassIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  useTrainers,
  useRequestTrainerChange,
  useMyTrainerRequests,
} from "../../../hooks/useTrainers";
import {
  PendingRequestView,
  RequestHistoryView,
  TrainerSelectionForm,
} from "./trainerRequest";

export default function RequestTrainerModal({
  open,
  onClose,
  onTrainerRequested,
}) {
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [remark, setRemark] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [viewMode, setViewMode] = useState("auto"); // "auto" | "form" | "history"

  // React Query Hook for all trainers from /trainer_assign/all-trainers
  const { data: trainersData, isLoading: trainersLoading } = useTrainers({
    enabled: open,
  });

  // React Query Hook for institute's own requests from /trainer_assign/my-requests
  const {
    data: myRequestsResponse,
    isLoading: myRequestsLoading,
    refetch: refetchMyRequests,
  } = useMyTrainerRequests(
    { page: historyPage, limit: 5 },
    { enabled: open }
  );

  // React Query Mutation Hook for requesting trainer change
  const requestChangeMutation = useRequestTrainerChange();

  const myRequestsList = myRequestsResponse?.data || [];
  const totalHistoryPages = myRequestsResponse?.total_pages || 1;
  const pendingRequest = myRequestsList.find(
    (r) => String(r.status).toLowerCase() === "pending"
  );
  const hasPendingRequest = Boolean(pendingRequest);

  // Reset selection and view mode when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTrainer(null);
      setRemark("");
      setHistoryPage(1);
      setViewMode("auto");
    }
  }, [open]);

  // Format trainers from query response
  const trainersList = useMemo(() => {
    if (trainersData && Array.isArray(trainersData) && trainersData.length > 0) {
      return trainersData.map((t, idx) => {
        const fdpList = (t.domains?.fdp || []).map((d) =>
          typeof d === "string" ? d : d.domain_name
        );
        const techcampList = (t.domains?.techcamp || []).map((d) =>
          typeof d === "string" ? d : d.domain_name
        );
        const allDomains = Array.from(new Set([...fdpList, ...techcampList]));

        return {
          id: t.trainer_id || t.id || idx + 1,
          fullname: t.trainer_name || t.fullname || t.name || `Trainer ${idx + 1}`,
          designation: t.designation || "Tech Trainer",
          domains: allDomains,
          fdpDomains: fdpList,
          techcampDomains: techcampList,
          rating: t.rating || "4.8",
          sessions: t.sessions || "15",
          email: t.email || "",
          phone: t.phone || t.mobile_number || "",
        };
      });
    }
    return [];
  }, [trainersData]);

  const handleRequest = async () => {
    if (!selectedTrainer) return;

    try {
      const payload = {
        trainer_id: selectedTrainer.id,
        remark: remark.trim() || "",
      };

      const res = await requestChangeMutation.mutateAsync(payload);
      refetchMyRequests();

      if (onTrainerRequested) {
        onTrainerRequested({
          success: true,
          trainer: selectedTrainer,
          message:
            res?.message ||
            `Trainer change request for ${selectedTrainer.fullname} submitted successfully and sent for review!`,
        });
      }

      onClose();
      setRemark("");
      setSelectedTrainer(null);
    } catch (error) {
      console.error("Error submitting trainer change request:", error);
      const errMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit trainer change request.";

      if (onTrainerRequested) {
        onTrainerRequested({
          success: false,
          error: errMsg,
          trainer: selectedTrainer,
        });
      }

      onClose();
      setRemark("");
      setSelectedTrainer(null);
    }
  };

  const submitting = requestChangeMutation.isLoading;
  const isHistoryView = viewMode === "history";
  const isPendingView = !isHistoryView && (viewMode === "pending" || hasPendingRequest);

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          fontFamily: '"Poppins", sans-serif',
          maxWidth: { xs: "95vw", sm: "760px", md: "800px" },
          borderRadius: "16px",
          p: { xs: 2, sm: 3 },
          boxShadow: "0 24px 48px rgba(0, 0, 0, 0.16)",
          maxHeight: "92vh",
          backgroundColor: "#FFFFFF",
        },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          p: 0,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #F1F5F9",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {isHistoryView ? (
            <IconButton
              onClick={() => setViewMode(hasPendingRequest ? "auto" : "form")}
              size="small"
              sx={{
                color: "#2563EB",
                backgroundColor: "#EFF6FF",
                "&:hover": { backgroundColor: "#DBEAFE" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                backgroundColor: isPendingView ? "#FFFBEB" : "#EFF6FF",
                border: isPendingView ? "1px solid #FEF3C7" : "1px solid #DBEAFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: isPendingView ? "#D97706" : "#2563EB",
              }}
            >
              {isPendingView ? (
                <HourglassIcon sx={{ fontSize: "1.3rem" }} />
              ) : (
                <PeopleIcon sx={{ fontSize: "1.3rem" }} />
              )}
            </Box>
          )}

          <Box>
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: "1.15rem",
                color: "#0F172A",
                letterSpacing: "-0.01em",
              }}
            >
              {isHistoryView
                ? "Request History"
                : isPendingView
                ? "Trainer Change Request"
                : "Request Trainer"}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontSize: "12px",
                color: "#64748B",
              }}
            >
              {isHistoryView
                ? "View all past trainer change requests for your institute"
                : isPendingView
                ? "Your request is currently awaiting coordinator review"
                : "Submit a request to change your institute's assigned trainer"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!isHistoryView && (
            <Tooltip title="View Request History" arrow>
              <IconButton
                onClick={() => setViewMode("history")}
                size="small"
                sx={{
                  color: "#64748B",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  "&:hover": { color: "#2563EB", backgroundColor: "#EFF6FF", borderColor: "#BFDBFE" },
                }}
              >
                <HistoryIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#94A3B8",
              "&:hover": { color: "#475569", backgroundColor: "#F1F5F9" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Dialog Body */}
      <DialogContent
        sx={{
          p: 0,
          py: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          fontFamily: '"Poppins", sans-serif',
          overflowY: "auto",
        }}
      >
        {isHistoryView ? (
          <RequestHistoryView
            loading={myRequestsLoading}
            requestsList={myRequestsList}
            totalPages={totalHistoryPages}
            currentPage={historyPage}
            onPageChange={setHistoryPage}
          />
        ) : isPendingView && pendingRequest ? (
          <PendingRequestView pendingRequest={pendingRequest} />
        ) : (
          <TrainerSelectionForm
            trainersLoading={trainersLoading}
            trainersList={trainersList}
            selectedTrainer={selectedTrainer}
            onSelectTrainer={setSelectedTrainer}
            remark={remark}
            onChangeRemark={setRemark}
          />
        )}
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions
        sx={{
          p: 0,
          pt: 2.5,
          borderTop: "1px solid #F1F5F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        {isHistoryView ? (
          <>
            <Box>
              {!hasPendingRequest && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setViewMode("form")}
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    textTransform: "none",
                    borderRadius: "8px",
                    color: "#2563EB",
                    fontWeight: 600,
                    fontSize: "0.88rem",
                    "&:hover": { backgroundColor: "#EFF6FF" },
                  }}
                >
                  New Request
                </Button>
              )}
            </Box>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                borderColor: "#E2E8F0",
                color: "#64748B",
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "0.88rem",
                fontWeight: 500,
                px: 3,
                py: 0.75,
                "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#F8FAFC" },
              }}
            >
              Close
            </Button>
          </>
        ) : isPendingView ? (
          <>
            <Button
              startIcon={<HistoryIcon />}
              onClick={() => setViewMode("history")}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                textTransform: "none",
                borderRadius: "8px",
                color: "#2563EB",
                fontWeight: 600,
                fontSize: "0.88rem",
                "&:hover": { backgroundColor: "#EFF6FF" },
              }}
            >
              View Request History
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                borderColor: "#E2E8F0",
                color: "#64748B",
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "0.88rem",
                fontWeight: 500,
                px: 3,
                py: 0.75,
                "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#F8FAFC" },
              }}
            >
              Close
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={submitting}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                textTransform: "none",
                borderRadius: "8px",
                color: "#64748B",
                fontWeight: 600,
                fontSize: "0.88rem",
                px: 2.5,
                py: 0.75,
                "&:hover": { backgroundColor: "#F1F5F9" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRequest}
              disabled={submitting || !selectedTrainer}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "0.88rem",
                px: 3,
                py: 0.75,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: "#93C5FD", color: "#FFFFFF" },
              }}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
              ) : (
                "Submit Request"
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
